import { config } from '../config';
import { log } from '../app/logger';
import { errorMessage } from '../app/errors';
import type { AudioPlatform, MediaStreamLike } from './audioPlatform';
import type { AudioCapabilityReport, MimeSupport } from '../types';

/**
 * AUD-1's first question: **what does this browser actually emit?**
 *
 * AUD-1 says "store the emitted blob with its actual MIME type; playback uses the stored blob
 * verbatim, with no transcoding on any path." The design (§0.1, A-2) expects fragmented
 * `audio/mp4` on iOS. Expecting is not knowing, and the answer changes what U-11 stores, what the
 * archive carries, and whether a chunk sequence can be concatenated at all — so it is probed and
 * reported, never assumed.
 *
 * The support matrix and the *default* are both captured because they answer different questions:
 * `isTypeSupported` says what the browser will accept if asked, while `new MediaRecorder(stream)`
 * with no options says what it picks for itself — and the second is what the product will be
 * carrying if it does not pass a `mimeType`.
 */

const MODULE = 'audio-caps';

/**
 * Probes support without touching the microphone.
 *
 * `isTypeSupported` needs no stream and no permission, so this runs on screen entry and gives
 * Andrew the container answer before he has granted anything. `defaultMime` needs a real stream and
 * is filled in later by `probeDefaultMime`.
 */
export async function probeCapabilities(platform: AudioPlatform): Promise<AudioCapabilityReport> {
  const supportMatrix: MimeSupport[] = config.AUDIO_MIME_CANDIDATES.map((mime) => ({
    mime,
    supported: platform.hasMediaRecorder && platform.isTypeSupported(mime),
  }));

  const permissionState = await platform.queryMicrophonePermission();

  const report: AudioCapabilityReport = {
    mediaRecorderSupported: platform.hasMediaRecorder,
    getUserMediaSupported: platform.hasGetUserMedia,
    permissionState,
    supportMatrix,
    defaultMime: null,
    probedAt: Date.now(),
  };

  log.info(MODULE, 'capability probe', {
    mediaRecorder: report.mediaRecorderSupported,
    getUserMedia: report.getUserMediaSupported,
    permissionState,
    supported: supportMatrix.filter((m) => m.supported).map((m) => m.mime),
  });

  return report;
}

/**
 * What the browser chooses when nothing is requested.
 *
 * Constructs a recorder and reads `mimeType` **without starting it**, so no audio is captured and
 * no recording indicator lights for a capability check.
 *
 * **A `null` here is usually not a failure.** The MediaRecorder spec allows `mimeType` to be the
 * empty string until recording starts when no type was requested, and Chromium does exactly that —
 * confirmed by driving a real browser during U-1. So `null` means "the browser would not commit
 * before recording", and the authoritative answer for AUD-1 is `SegmentRecord.actualMime`, read off
 * a recorder that has actually started. Reported separately rather than conflated, because "the
 * browser said nothing yet" and "there is no MIME type" are different facts.
 *
 * Returns `null` rather than throwing: a browser that refuses to construct a recorder has answered
 * the question, and the answer belongs in the report next to the rest of the matrix. The failure is
 * logged, not swallowed (NFR-8).
 */
export function probeDefaultMime(platform: AudioPlatform, stream: MediaStreamLike): string | null {
  try {
    const recorder = platform.createRecorder(stream, null);
    const mime = recorder.mimeType;
    return mime === '' ? null : mime;
  } catch (cause) {
    log.warn(MODULE, 'could not construct a default MediaRecorder to read its mimeType', {
      error: errorMessage(cause),
    });
    return null;
  }
}
